/* eslint-disable @typescript-eslint/ban-ts-comment */
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  Box,
  Button,
  Checkbox,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Grid,
  Chip,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import Select, { StylesConfig } from 'react-select'
import makeAnimated from 'react-select/animated'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import { useCreateUserMutation, useUpdateUserMutation } from '../../redux/api/userApi'
import { IUser, RoleOption } from '../../types'
import UserDealerGroupAdd from './UserDealerGroupAdd'
import UserAftermarketClientAdd from './UserAftermarketClientAdd'
import UserDealerIdAdd from './UserDealerIdAdd'

const validationSchema = yup.object({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  phone: yup.string(),
})

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
    height: '95vh',
    overflow: 'auto',
  },
  headerStyle: {
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    mb: 4,
  },
  noMargin: {
    margin: '0px!important',
  },
  alignItemsCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  clickable: {
    cursor: 'pointer',
  },
  fontStyleForm: {
    fontSize: '1rem',
  },
  dealerGroupContainer: {
    display: 'flex',
    marginTop: '.5rem',
  },
  dealerAccordion: {
    width: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    marginTop: '8px',
  },

  dealerAccordionHeader: {
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
  },

  dealerAccordionBody: {
    padding: '10px 12px',
    borderTop: '1px solid #e0e0e0',
  },
  dealerIdChipWrap: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    maxHeight: '60px',
    overflowY: 'auto',
  },
}

const customStyles: StylesConfig<any, false> = {
  menu: (provided) => ({
    ...provided,
    position: 'absolute',
    zIndex: 9999,
  }),
  control: (baseStyles) => ({
    ...baseStyles,
    height: '56px',
  }),
}

const animatedComponents = makeAnimated()

interface Option {
  value: string | number
  label: string
}

const DEFAULT_ROLE_ITEM: RoleOption = {
  applicationId: -1,
  userRoleId: -1,
  isDefaultFlg: false,
}

interface Props {
  onClose: () => void
  refetch: () => void
  onUserUpdated?: (data: Partial<IUser>) => void
  user?: IUser
  options?: Option[]
  applications?: Option[]
  open: boolean
  dealerGroupNumberList: {
    dealerGroupName: string
    dealerGroupNumber: string
    dealerGroup: string
  }[]
  aftermarketClientList: { clientId: string; dealerGroupNumber: string; client: string }[]
}

export const UserForm = ({
  onClose,
  refetch,
  onUserUpdated,
  options = [],
  applications = [],
  user,
  open,
  dealerGroupNumberList,
  aftermarketClientList,
}: Props) => {
  const [roles, setRoles] = React.useState<RoleOption[]>([DEFAULT_ROLE_ITEM])
  const [createUser, { isLoading, isError, error, isSuccess }] = useCreateUserMutation()
  const [
    updateUser,
    { isLoading: uIsLoading, isError: uIsError, error: uError, isSuccess: uIsSuccess },
  ] = useUpdateUserMutation()
  const [openAddDealerGroup, setOpenAddDealerGroup] = useState<boolean>(false)
  const [openAddAftermarketClient, setOpenAddAftermarketClient] = useState<boolean>(false)
  const [openAddDealerId, setOpenAddDealerId] = useState<boolean>(false)
  const [openDealerAccordion, setOpenDealerAccordion] = useState<string | null>(null)
  const [allDealerGroups, setAllDealerGroups] = useState<any[]>([])

  const handleOpenAddDealerId = (): void => setOpenAddDealerId(true)
  const onCloseAddDealerId = (): void => setOpenAddDealerId(false)
  const toastId = React.useRef<React.ReactText | null>(null)

  const showProcessingToast = () => {
    if (!toastId.current) {
      toastId.current = toast.loading('Processing...', { position: 'top-right' })
    }
  }

  const closeProcessingToast = () => {
    if (toastId.current !== null) {
      toast.dismiss(toastId.current)
      toastId.current = null
    }
  }
  React.useEffect(() => {
    if (isSuccess) {
      toast.success('Success!')
      onClose()
      refetch()
    }
    if (isError) {
      if (Array.isArray((error as any)?.data?.error)) {
        const errors = error as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((error as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [isLoading])

  React.useEffect(() => {
    const fetchDealerGroups = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const baseUrl = process.env.REACT_APP_OPW_SERVICE_API_ENDPOINT
        const res = await fetch(`${baseUrl}/dealergroups/getDealerGroupsIds`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const data = await res.json()
        setAllDealerGroups(data || [])
      } catch (error) {
        console.error('Error fetching dealer groups:', error)
      }
    }

    if (open) {
      fetchDealerGroups()
    }
  }, [open])

  React.useEffect(() => {
    if (uIsSuccess) {
      toast.success('Success!')
      onClose()
      refetch()
    }
    if (uIsError) {
      if (Array.isArray((uError as any)?.data?.error)) {
        const errors = uError as any
        errors?.data.error.forEach((el: any) =>
          toast.error(el?.message ?? 'Something Failed!', {
            position: 'top-right',
          }),
        )
      } else {
        toast.error((uError as any)?.data?.message ?? 'Something Failed!', {
          position: 'top-right',
        })
      }
    }
  }, [uIsLoading])

  const formik = useFormik({
    initialValues: {
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phone: user?.phone ?? '',
      dealerGroupData: Array.isArray(user?.dealerGroupData) ? user?.dealerGroupData ?? [] : [],
      aftermarketClient: user?.aftermarketClient ? user.aftermarketClient : [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (!isValid()) {
        toast.error('Please choose at least default application!')
        return
      }
      showProcessingToast()
      // Preprocess aftermarketClient to ensure all values are dealerGroupNumbers
      // const processedAftermarketClient = values.aftermarketClient.map((client) => {
      //   // Check if the client value is already a dealerGroupNumber in aftermarketClientList
      //   const foundByDealerGroup = aftermarketClientList.find(
      //     (item) => item.dealerGroupNumber === client,
      //   )
      //   if (foundByDealerGroup) {
      //     return foundByDealerGroup.dealerGroupNumber
      //   }

      //   // If not found as dealerGroupNumber, check if it matches a client name
      //   const foundByClient = aftermarketClientList.find((item) => item.client === client)
      //   if (foundByClient) {
      //     return foundByClient.dealerGroupNumber
      //   }

      //   // If not found in either, return the original value (or handle as needed)
      //   return client
      // })

      // const uniqueAftermarketClient = Array.from(new Set(processedAftermarketClient)).filter(
      //   (item: any) => item !== undefined && item !== null,
      // )
      const uniqueAftermarketClient = Array.from(new Set(values.aftermarketClient)).filter(
        (item: any) => item !== undefined && item !== null,
      )

      const newRoles = roles.filter(
        (role) => !(role.userRoleId === -1 && role.applicationId === -1),
      )

      if (user?.userId) {
        updateUser({
          userId: user?.userId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          dealerGroupData: values.dealerGroupData,
          aftermarketClient: uniqueAftermarketClient,
          newUserRoleIdList: newRoles,
        }).finally(() => closeProcessingToast())
        onUserUpdated?.({
          userId: user.userId,
          dealerGroupData: values.dealerGroupData,
          aftermarketClient: uniqueAftermarketClient,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
        })
      } else {
        createUser({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          dealerGroupData: values.dealerGroupData,
          aftermarketClient: uniqueAftermarketClient,
          newUserRoleIdList: newRoles,
        }).finally(() => closeProcessingToast())
      }

      reset()
    },
  })

  React.useEffect(() => {
    if (!open) return

    if (user) {
      formik.setValues({
        email: user.email ?? '',
        phone: user.phone ?? '',
        dealerGroupData: Array.isArray(user.dealerGroupData) ? user.dealerGroupData : [],
        aftermarketClient: Array.isArray(user.aftermarketClient)
          ? user.aftermarketClient.map((item: any) => item.clientId || item)
          : [],
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
      })

      if (user.userRoleList) {
        setRoles(
          user.userRoleList.map((role) => ({
            applicationId: role.applicationId,
            userRoleId: role.userRoleId,
            isDefaultFlg: role.isDefaultFlg,
          })),
        )
      } else {
        setRoles([DEFAULT_ROLE_ITEM])
      }
    } else {
      formik.setValues({
        email: '',
        phone: '',
        dealerGroupData: [],
        aftermarketClient: [],
        firstName: '',
        lastName: '',
      })
      setRoles([DEFAULT_ROLE_ITEM])
    }
  }, [open, user])

  // Add dealer Group Modal handling
  const onCloseAddDealerGroup = (): void => {
    setOpenAddDealerGroup(false)
  }

  const handleOpenAddDealerGroup = (): void => {
    setOpenAddDealerGroup(true)
  }

  // Add Aftermarket Client Modal handling
  const onCloseAddAftermarketClient = (): void => {
    setOpenAddAftermarketClient(false)
  }

  const handleOpenAddAftermarketClient = (): void => {
    setOpenAddAftermarketClient(true)
  }

  const reset = () => {
    formik.resetForm()
    onClose()

    if (!user) {
      setRoles([DEFAULT_ROLE_ITEM])
    }
  }

  const addOption = () => {
    if (roles.length >= applications.length) {
      return
    }

    setRoles([...roles, DEFAULT_ROLE_ITEM])
  }

  const removeRole = (idx: number) => {
    if (roles.length === 1) {
      return
    }

    setRoles([...roles.slice(0, idx), ...roles.slice(idx + 1)])
  }

  const getApplicationOptions = (roleApplicationId: any) => {
    const prevApplications = roles
      .filter((role) => role.applicationId !== -1 && role.applicationId !== roleApplicationId)
      .map((role) => role.applicationId)

    return applications.filter(
      (application) => !prevApplications.includes(Number(application.value)),
    )
  }

  const isValid = (): boolean => {
    const tRoles = roles.filter((role) => role.applicationId !== -1 && role.userRoleId !== -1)
    if (!tRoles.length) {
      return false
    }

    return tRoles.some((role) => role.isDefaultFlg)
  }

  // React.useEffect(() => {
  //   if (user) {
  //     formik.setValues({
  //       email: user.email ?? '',
  //       firstName: user.firstName ?? '',
  //       lastName: user.lastName ?? '',
  //       phone: user.phone ?? '',
  //       dealerGroupData: Array.isArray(user.dealerGroupData) ? user.dealerGroupData : [],
  //       aftermarketClient: Array.isArray(user.aftermarketClient)
  //         ? user.aftermarketClient.map((item: any) => item.dealerGroupNumber || item)
  //         : [],
  //     })
  //   } else {
  //     formik.resetForm()
  //   }

  //   if (user?.userRoleList) {
  //     setRoles(
  //       user.userRoleList.map((role) => ({
  //         applicationId: role.applicationId,
  //         userRoleId: role.userRoleId,
  //         isDefaultFlg: role.isDefaultFlg,
  //       })),
  //     )
  //   } else {
  //     setRoles([DEFAULT_ROLE_ITEM])
  //   }
  // }, [user])

  const handleDeleteAftermarketClient = (itemToDelete: string) => {
    const newAftermarketClientList = formik.values.aftermarketClient.filter(
      (item) => item !== itemToDelete,
    )
    formik.setFieldValue('aftermarketClient', newAftermarketClientList)
  }
  const handleAddAllDealerIds = (dealerGroupNumber: string) => {
    const groupFromApi = allDealerGroups.find((g) => g.dealerGroupNumber === dealerGroupNumber)

    const allIds =
      groupFromApi?.dealerIdList?.map((d: any) => d.dealerId || '').filter(Boolean) || []

    if (!allIds.length) {
      toast.error(`No Dealer IDs for ${dealerGroupNumber}`)
      return
    }

    const updated = formik.values.dealerGroupData.map((group) => {
      if (group.dealerGroupNumber === dealerGroupNumber) {
        return {
          ...group,
          dealerIdList: allIds.map((id: string) => ({ dealerId: id })),
        }
      }
      return group
    })

    formik.setFieldValue('dealerGroupData', updated)
  }
  const handleRemoveAllDealerIds = (dealerGroupNumber: string) => {
    const updated = formik.values.dealerGroupData.map((group) => {
      if (group.dealerGroupNumber === dealerGroupNumber) {
        return {
          ...group,
          dealerIdList: [],
        }
      }
      return group
    })

    formik.setFieldValue('dealerGroupData', updated)
  }

  const handleRemoveDealerId = (dealerGroupNumber: string, dealerIdToRemove: string) => {
    const updated = formik.values.dealerGroupData.map((group) => {
      if (group.dealerGroupNumber === dealerGroupNumber) {
        return {
          ...group,
          dealerIdList: Array.isArray(group.dealerIdList)
            ? group.dealerIdList.filter((d) => d.dealerId !== dealerIdToRemove)
            : [],
        }
      }
      return group
    })

    formik.setFieldValue('dealerGroupData', updated)
    onUserUpdated
    onUserUpdated
    onUserUpdated?.({
      userId: user?.userId,
      dealerGroupData: updated,
    })
  }
  const sortedDealerGroupData = React.useMemo(() => {
    if (!Array.isArray(formik.values.dealerGroupData)) return []

    return [...formik.values.dealerGroupData]
      .map((group) => {
        const dealerGroupInfo = dealerGroupNumberList.find(
          (g) => g.dealerGroupNumber === group.dealerGroupNumber,
        )
        const displayLabel =
          dealerGroupInfo?.dealerGroupName ||
          dealerGroupInfo?.dealerGroup ||
          group.dealerGroupNumber

        const validAndSortedDealerIds = Array.isArray(group.dealerIdList)
          ? [...group.dealerIdList]
              .filter((item) => {
                const id = typeof item === 'string' ? item : item?.dealerId
                return id && id.trim() !== ''
              })
              .sort((a, b) => {
                const idA = typeof a === 'string' ? a : a.dealerId || ''
                const idB = typeof b === 'string' ? b : b.dealerId || ''
                return idA.localeCompare(idB)
              })
          : []

        return { ...group, displayLabel, dealerIdList: validAndSortedDealerIds }
      })
      .sort((a, b) => a.displayLabel.toLowerCase().localeCompare(b.displayLabel.toLowerCase()))
  }, [formik.values.dealerGroupData, dealerGroupNumberList])

  return (
    <React.Fragment>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={styles.container}>
          <Box sx={styles.headerStyle}>
            <Typography id='modal-modal-title' variant='h5' component='h2' color='white'>
              User Details
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  id='firstName'
                  name='firstName'
                  value={formik.values.firstName}
                  placeholder='First Name'
                  onChange={formik.handleChange}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
                <TextField
                  fullWidth
                  id='lastName'
                  name='lastName'
                  placeholder='Last Name'
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
                <TextField
                  fullWidth
                  id='email'
                  name='email'
                  placeholder='Email'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <Grid xs={12} container sx={styles.alignItemsCenter}>
                  <Grid
                    item
                    sx={{ ...styles.alignItemsCenter, ...styles.clickable }}
                    onClick={() => {
                      handleOpenAddDealerGroup()
                    }}
                  >
                    Dealer Groups &nbsp; <AddCircleRoundedIcon />
                  </Grid>

                  <Grid container item xs={12} spacing={1} style={styles.dealerGroupContainer}>
                    {sortedDealerGroupData.map((group, index) => (
                      <Grid key={index} item>
                        <Chip
                          label={group.displayLabel}
                          variant='outlined'
                          onDelete={() =>
                            formik.setFieldValue(
                              'dealerGroupData',
                              formik.values.dealerGroupData.filter(
                                (g) => g.dealerGroupNumber !== group.dealerGroupNumber,
                              ),
                            )
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid xs={12} container sx={styles.alignItemsCenter}>
                  <Grid
                    item
                    sx={{ ...styles.alignItemsCenter, ...styles.clickable }}
                    onClick={handleOpenAddDealerId}
                  >
                    Dealer ID / Store Number &nbsp; <AddCircleRoundedIcon />
                  </Grid>
                  <Grid container item xs={12} spacing={1} style={styles.dealerGroupContainer}>
                    {Array.isArray(formik.values.dealerGroupData) &&
                      [...formik.values.dealerGroupData]
                        .sort((a, b) =>
                          (a.dealerGroupNumber || '').localeCompare(b.dealerGroupNumber || ''),
                        )
                        .map((group) => {
                          const isOpen = openDealerAccordion === group.dealerGroupNumber

                          const dealerGroupInfo = dealerGroupNumberList.find(
                            (g) => g.dealerGroupNumber === group.dealerGroupNumber,
                          )

                          const groupLabel =
                            dealerGroupInfo?.dealerGroupName ||
                            dealerGroupInfo?.dealerGroup ||
                            group.dealerGroupNumber

                          const dealerIdsRaw =
                            Array.isArray(group.dealerIdList) && group.dealerIdList.length
                              ? group.dealerIdList
                              : []

                          // --- NEW: Split existing vs newly added Dealer IDs ---
                          const originalDealerIds =
                            user?.dealerGroupData
                              ?.find((g) => g.dealerGroupNumber === group.dealerGroupNumber)
                              ?.dealerIdList?.map((d) => d.dealerId)
                              .filter(Boolean) || []

                          const dealerIds = [
                            ...dealerIdsRaw.filter((d) => originalDealerIds.includes(d.dealerId)),
                            ...dealerIdsRaw.filter((d) => !originalDealerIds.includes(d.dealerId)),
                          ]
                          // --- END NEW ---

                          return (
                            <Box key={group.dealerGroupNumber} sx={styles.dealerAccordion}>
                              {/* HEADER */}
                              <Box
                                sx={styles.dealerAccordionHeader}
                                onClick={() =>
                                  setOpenDealerAccordion(isOpen ? null : group.dealerGroupNumber)
                                }
                              >
                                <Typography variant='body1'>{groupLabel}</Typography>
                                <Typography variant='body2'>{isOpen ? '−' : '+'}</Typography>
                              </Box>

                              {/* BODY */}
                              {isOpen && (
                                <Box sx={styles.dealerAccordionBody}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'flex-end',
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Button
                                      size='small'
                                      variant='contained'
                                      onClick={() => handleAddAllDealerIds(group.dealerGroupNumber)}
                                      disabled={
                                        !allDealerGroups.find(
                                          (g) => g.dealerGroupNumber === group.dealerGroupNumber,
                                        )?.dealerIdList?.length
                                      }
                                    >
                                      Add All
                                    </Button>

                                    <Button
                                      size='small'
                                      variant='outlined'
                                      color='error'
                                      onClick={() =>
                                        handleRemoveAllDealerIds(group.dealerGroupNumber)
                                      }
                                      disabled={!dealerIds.length}
                                    >
                                      Remove All
                                    </Button>
                                  </Box>

                                  {dealerIds.length ? (
                                    <Box>
                                      {dealerIds.filter(
                                        (d) =>
                                          d.dealerId && !originalDealerIds.includes(d.dealerId),
                                      ).length > 0 && (
                                        <Box sx={styles.dealerIdChipWrap}>
                                          {dealerIds
                                            .filter(
                                              (d) =>
                                                d.dealerId &&
                                                !originalDealerIds.includes(d.dealerId),
                                            )
                                            .map((d, idx) => (
                                              <Chip
                                                key={`new-${group.dealerGroupNumber}-${d.dealerId}-${idx}`}
                                                label={d.dealerId}
                                                size='small'
                                                variant='outlined'
                                                color='primary'
                                                onDelete={() =>
                                                  handleRemoveDealerId(
                                                    group.dealerGroupNumber,
                                                    d.dealerId,
                                                  )
                                                }
                                              />
                                            ))}
                                        </Box>
                                      )}

                                      {dealerIds.filter(
                                        (d) => d.dealerId && originalDealerIds.includes(d.dealerId),
                                      ).length > 0 && (
                                        <Box sx={{ ...styles.dealerIdChipWrap, mt: 1 }}>
                                          {dealerIds
                                            .filter(
                                              (d) =>
                                                d.dealerId &&
                                                originalDealerIds.includes(d.dealerId),
                                            )
                                            .map((d, idx) => (
                                              <Chip
                                                key={`old-${group.dealerGroupNumber}-${d.dealerId}-${idx}`}
                                                label={d.dealerId}
                                                size='small'
                                                variant='outlined'
                                                onDelete={() =>
                                                  handleRemoveDealerId(
                                                    group.dealerGroupNumber,
                                                    d.dealerId,
                                                  )
                                                }
                                              />
                                            ))}
                                        </Box>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant='body2' color='text.secondary'>
                                      No Dealer IDs selected
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )
                        })}
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  id='phone'
                  name='phone'
                  placeholder='Phone'
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />

                <Grid xs={12} container sx={styles.alignItemsCenter}>
                  <Grid
                    item
                    sx={{ ...styles.alignItemsCenter, ...styles.clickable }}
                    onClick={() => {
                      handleOpenAddAftermarketClient()
                    }}
                  >
                    Aftermarket Clients &nbsp; <AddCircleRoundedIcon />
                  </Grid>
                  <Grid container item xs={12} spacing={1} style={styles.dealerGroupContainer}>
                    {Array.isArray(formik?.values?.aftermarketClient) &&
                      formik.values.aftermarketClient.map((item, index) => {
                        const client =
                          aftermarketClientList?.find(
                            (group) => String(group.clientId) === String(item),
                          )?.client || item

                        return (
                          <Grid key={index} item>
                            <Chip
                              label={client}
                              variant='outlined'
                              onDelete={() => handleDeleteAftermarketClient(item)}
                            />
                          </Grid>
                        )
                      })}
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant='h6' component='h5'>
                    Applications
                  </Typography>
                  <IconButton onClick={addOption}>
                    <AddCircleIcon color='primary' />
                  </IconButton>
                </Box>

                <Typography
                  sx={styles.noMargin}
                  variant='subtitle1'
                  component='span'
                  align='center'
                  color='lightgrey'
                >
                  Add applications with roles and select one as a default option.
                </Typography>
                {roles.map((role, idx) => (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    key={`role-${idx}`}
                  >
                    <Select
                      closeMenuOnSelect
                      components={animatedComponents}
                      placeholder='Application'
                      value={applications.find(
                        (application) => application.value === role.applicationId,
                      )}
                      // @ts-ignore
                      onChange={({ value }) => {
                        setRoles([
                          ...roles.slice(0, idx),
                          {
                            applicationId: value,
                            userRoleId: role.userRoleId,
                            isDefaultFlg: role.isDefaultFlg,
                          },
                          ...roles.slice(idx + 1),
                        ])
                      }}
                      options={getApplicationOptions(role.applicationId)}
                      className='react-select-container'
                      styles={{
                        ...customStyles,
                        control: (baseStyles) => ({
                          ...baseStyles,
                          height: '56px',
                        }),
                      }}
                    />
                    <Select
                      closeMenuOnSelect
                      components={animatedComponents}
                      placeholder='User Role'
                      value={options.find((option) => option.value === role.userRoleId)}
                      // @ts-ignore
                      onChange={({ value }) => {
                        setRoles([
                          ...roles.slice(0, idx),
                          {
                            applicationId: role.applicationId,
                            userRoleId: value,
                            isDefaultFlg: role.isDefaultFlg,
                          },
                          ...roles.slice(idx + 1),
                        ])
                      }}
                      options={options}
                      className='react-select-container'
                      styles={{
                        ...customStyles,
                        control: (baseStyles) => ({
                          ...baseStyles,
                          height: '56px',
                        }),
                      }}
                    />
                    <Tooltip title='Make default' arrow>
                      <Checkbox
                        checked={!!role.isDefaultFlg}
                        inputProps={{ 'aria-label': 'controlled' }}
                        onChange={(e) => {
                          setRoles([
                            ...roles
                              .slice(0, idx)
                              .map((role) => ({ ...role, isDefaultFlg: false })),
                            {
                              applicationId: role.applicationId,
                              userRoleId: role.userRoleId,
                              isDefaultFlg: e.target.checked,
                            },
                            ...roles
                              .slice(idx + 1)
                              .map((role) => ({ ...role, isDefaultFlg: false })),
                          ])
                        }}
                      />
                    </Tooltip>
                    <Tooltip title='Delete' arrow>
                      <IconButton onClick={() => removeRole(idx)}>
                        <CancelIcon color='error' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ textAlign: 'right', mt: '30px' }}>
                <Button
                  variant='outlined'
                  size='large'
                  sx={{ mr: 2, borderRadius: '20px' }}
                  color='error'
                  onClick={reset}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  sx={{ borderRadius: '20px' }}
                  size='large'
                  type='submit'
                  disabled={!isValid()}
                >
                  Submit
                </Button>
              </Box>

              <UserDealerGroupAdd
                openAddDealerGroup={openAddDealerGroup}
                onCloseAddDealerGroup={onCloseAddDealerGroup}
                formik={formik}
                dealerGroupNumberList={dealerGroupNumberList}
              />
              <UserAftermarketClientAdd
                openAddDealerGroup={openAddAftermarketClient}
                onCloseAddDealerGroup={onCloseAddAftermarketClient}
                formik={formik}
                dealerGroupNumberList={dealerGroupNumberList}
                aftermarketClientList={aftermarketClientList}
              />
              <UserDealerIdAdd
                openDealerIdModal={openAddDealerId}
                onCloseDealerIdModal={onCloseAddDealerId}
                formik={formik}
              />
            </form>
          </Box>
        </Box>
      </Modal>
    </React.Fragment>
  )
}
